import Autocomplete, { type AutocompleteProps } from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { forwardRef, useMemo } from 'react';

import { Checkbox } from '../Checkbox';
import type { ItemComponentProps, OptionSelectionProps } from '../types';

export interface ItemWithSelectAllComponentProps extends ItemComponentProps {
  isAllSelected?: boolean;
  isSomeSelected?: boolean;
}

type SearchMultipleSelectionWithSelectAllOptionProps = Omit<
  AutocompleteProps<OptionSelectionProps, true, boolean, false>,
  'renderInput'
> &
  Pick<
    TextFieldProps,
    | 'label'
    | 'error'
    | 'helperText'
    | 'required'
    | 'variant'
    | 'color'
    | 'inputRef'
    | 'InputProps'
    | 'placeholder'
    | 'slotProps'
  > & {
    itemComponent?: React.FC<ItemWithSelectAllComponentProps>;
    slotProps?: TextFieldProps['slotProps'] & {
      selectAllOption?: Omit<OptionSelectionProps, 'value'>;
    };
  };

function DefaultItem(props: Readonly<ItemWithSelectAllComponentProps>) {
  const { option, selected, isAllSelected, isSomeSelected } = props;
  return (
    <Checkbox
      label={option.label}
      value={option.value}
      checked={isAllSelected ? true : selected}
      indeterminate={option.value === 'all' ? isSomeSelected : false}
    />
  );
}

const SearchMultipleSelectionWithSelectAllOption = forwardRef(
  (props: Readonly<SearchMultipleSelectionWithSelectAllOptionProps>, ref) => {
    const {
      placeholder,
      helperText,
      error: hasError,
      label,
      options: baseOptions,
      required: isRequired,
      variant,
      color,
      InputProps,
      inputRef,
      itemComponent: ItemComponent = DefaultItem,
      value,
      onChange,
      multiple = true,
      slotProps,
      ...restProps
    } = props;

    const isAllSelected = useMemo(() => {
      return value && value.length > 0 && value.some((option) => option.value === 'all');
    }, [value]);

    const isSomeSelected = useMemo(() => {
      return value && value.length > 0 && !isAllSelected;
    }, [value, isAllSelected]);

    const options = useMemo(() => {
      if (baseOptions.length === 0) {
        return [];
      }

      return [
        {
          label: slotProps?.selectAllOption?.label || `เลือกทั้งหมด (${baseOptions.length})`,
          value: 'all',
        },
        ...baseOptions,
      ];
    }, [baseOptions]);

    const selectAllOption = useMemo(
      () => options.find((option) => option.value === 'all'),
      [options],
    );

    const handleChange: SearchMultipleSelectionWithSelectAllOptionProps['onChange'] = (
      _,
      newValues,
      reason,
      details,
    ) => {
      // Single selection: keep only the last selected option
      if (!multiple) {
        const selectedOptions = newValues.slice(-1);
        onChange?.(_, selectedOptions, reason, details);
        return;
      }

      const { value: selectedValue } = details?.option ?? {};

      // If "Select All" clicked
      if (selectedValue === 'all') {
        if (reason === 'selectOption') {
          onChange?.(_, [selectAllOption as OptionSelectionProps], reason, details);
        } else {
          onChange?.(_, [], reason, details);
        }
        return;
      }

      // If "Select All" is selected and other option is selected, remove the selected option
      if (isAllSelected && selectedValue) {
        const valuesWithoutSelected = options.filter(
          (option) => option.value !== selectedValue && option.value !== 'all',
        );
        onChange?.(_, valuesWithoutSelected, reason, details);
        return;
      }

      // If all options are selected, set "Select All" option
      if (selectAllOption && newValues.length === options.length - 1) {
        onChange?.(_, [selectAllOption as OptionSelectionProps], reason, details);
        return;
      }

      onChange?.(_, newValues, reason, details);
    };

    return (
      <Autocomplete
        disableCloseOnSelect
        ref={ref}
        multiple={multiple}
        options={options}
        onChange={handleChange}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 10],
                },
              },
            ],
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={value?.length ? '' : placeholder}
            error={hasError}
            helperText={helperText}
            required={isRequired}
            variant={variant}
            color={color}
            inputRef={inputRef}
            slotProps={{
              input: { ...params.InputProps, ...InputProps, ...slotProps?.input },
            }}
          />
        )}
        renderOption={({ key, onClick, ...restOptionProps }, option, { selected }) => {
          return (
            <MenuItem
              key={key}
              {...restOptionProps}
              value={option.value}
              onClick={(event) => {
                // The item's checkbox label is a native <label>, whose
                // default click behavior forwards the click to its hidden
                // <input> and focuses it. That focus jump — away from the
                // Autocomplete's own input — defeats MUI's click-inside-
                // listbox blur guard and closes the popup even with
                // disableCloseOnSelect set. Suppressing the browser default
                // blocks that forwarding while still letting this same
                // click select the option normally.
                event.preventDefault();
                onClick?.(event);
              }}
            >
              <ItemComponent
                option={option}
                selected={selected}
                isAllSelected={isAllSelected}
                isSomeSelected={isSomeSelected}
              />
            </MenuItem>
          );
        }}
        value={value}
        {...restProps}
      />
    );
  },
);

SearchMultipleSelectionWithSelectAllOption.displayName =
  'SearchMultipleSelectionWithSelectAllOption';

export type { SearchMultipleSelectionWithSelectAllOptionProps };
export { SearchMultipleSelectionWithSelectAllOption };
